import { useEffect, useRef } from 'react';

import { useCohortDefinitionStore } from '@verantos-dev/app-state-cohort-definition';
import { useCohortDefinitionQuery } from '@verantos-dev/feature-analytical-variables';
import type { CohortVersionParams } from '@verantos-dev/feature-analytical-variables';

/**
 * Explicit-save TanStack Query → Zustand sync.
 *
 * Re-hydrate the store whenever TQ hands back a NEW data reference and the
 * user has no local staged edits. This matches the legacy variant's pattern
 * and ensures server-confirmed updates (agent-driven attaches via the chat
 * resource_updated invalidation, socket reconciliation, etc.) flow into the
 * store immediately instead of stranding the criteria card on stale
 * "Resolving to an analytical variable…" placeholders (OR-6089).
 *
 * `hasStagedChanges` guards the "don't clobber user edits" case the prior
 * once-per-version gate was meant to address; matching by data reference
 * (not version id) means same-version mutations (e.g. AV attached to a
 * pending stub on the current draft version) re-hydrate cleanly.
 *
 * Hydration also waits for `isFetching: false`. Without this guard, the AV
 * edit page can mount mid-refetch (e.g. immediately after Save invalidates
 * the definition query) and seed the store from the stale cached payload —
 * losing the just-saved AV.
 */
export const useCohortDefinitionDataExplicitSave = (
  params: CohortVersionParams & { enabled?: boolean }
) => {
  const { enabled, ...queryParams } = params;
  const { data, isLoading, isFetching, error } = useCohortDefinitionQuery(queryParams, {
    enabled,
  });
  const setFromServer = useCohortDefinitionStore((s) => s.setFromServer);
  const setHasStagedChanges = useCohortDefinitionStore((s) => s.setHasStagedChanges);
  const lastProcessedDataRef = useRef<typeof data | undefined>();
  // The cohort whose definition this hook last hydrated. Used to distinguish the
  // FIRST load of a (freshly-switched) cohort from later background refetches.
  const hydratedCohortRef = useRef<string | undefined>();
  // The cohort VERSION this hook last hydrated. A change here within the SAME cohort
  // means the server minted a new version (e.g. the agent's edit promoted the cohort
  // to a fresh draft) — handled like an initial load by the guard below.
  const hydratedVersionRef = useRef<string | undefined>();

  useEffect(() => {
    if (!enabled || !data || isFetching) return;
    if (data === lastProcessedDataRef.current) return;

    // `hasStagedChanges` is overloaded: it means both "a draft exists" (set by
    // the on-subscribe `staged_changes_updated` socket event) AND "the user has
    // in-memory edits to protect". The guard below is meant for the SECOND case
    // — don't clobber the user's live edits with a stale refetch. But right
    // after a cohort switch the lifecycle has `reset()` the store (OR-6110), so
    // there are no in-memory edits to protect, and the socket may already have
    // flipped `hasStagedChanges` to true. Skipping here would strand the
    // freshly-opened cohort on an empty definition (blank criteria/index date)
    // until a hard refresh. So only apply the guard AFTER this cohort's
    // definition has been hydrated once; let the initial load through.
    const isInitialLoadForCohort = hydratedCohortRef.current !== queryParams.cohortId;
    // OR-6147: a server-minted NEW cohort version supersedes any local staged edits
    // on the prior version — those edits live on the old version and are stale.
    // Treat the first hydration of a new version like an initial load: always
    // hydrate, bypassing the staged guard. This is how a version-advancing agent
    // edit (any edit while the cohort isn't already an UNSAVED draft) live-renders:
    // the profile refetch advances `latestCohortVersionId`, the definition query
    // re-keys to it, and this branch hydrates the new version's definition. It also
    // makes the flow robust to refetch ORDERING — if the prior version's refetch
    // consumed the one-shot `agentRefetchPending` flag first, the version advance
    // still hydrates.
    const isNewVersionForCohort =
      !isInitialLoadForCohort && hydratedVersionRef.current !== queryParams.cohortVersionId;
    // When this refetch was driven by an agent cohort mutation (the user asked the
    // chat to change the cohort), the server-confirmed state wins over any local
    // staged edits — so bypass the guard and hydrate. Covers the IN-PLACE edit case
    // (same version) that `isNewVersionForCohort` doesn't. Otherwise keep protecting
    // the user's live edits from a stale background refetch.
    const agentRefetchPending = useCohortDefinitionStore.getState().agentRefetchPending;
    if (
      !isInitialLoadForCohort &&
      !isNewVersionForCohort &&
      useCohortDefinitionStore.getState().hasStagedChanges &&
      !agentRefetchPending
    )
      return;

    hydratedCohortRef.current = queryParams.cohortId;
    hydratedVersionRef.current = queryParams.cohortVersionId;
    lastProcessedDataRef.current = data;

    setFromServer(data.analyticalVariables, data.criteria, data.studyPeriod, {
      simpleEvents: data.simpleEvents,
      derivedEvents: data.derivedEvents,
      indexDate: data.indexDate,
      subjectVariables: data.subjectVariables,
    });
    // `setFromServer` clears `hasStagedChanges`; re-flag it when the loaded
    // definition is an uncommitted draft so the "Unsaved changes" pill shows
    // after navigating away and back (OR-6110).
    if (data.hasStagedChanges) setHasStagedChanges(true);
    // One-shot: the agent refetch has now hydrated, so clear the flag.
    if (agentRefetchPending) useCohortDefinitionStore.getState().setAgentRefetchPending(false);
  }, [
    enabled,
    data,
    isFetching,
    setFromServer,
    setHasStagedChanges,
    queryParams.cohortId,
    queryParams.cohortVersionId,
  ]);

  return { isLoading, error };
};
